import {useNavigate, useSearchParams} from "react-router-dom";
import {useEffect, useState} from "react";
import { useLocalStorage } from "../Context/LocalStorageContext";
import {StepDesc, StepHeader, StepSubheader} from "../Body/SectionHeader";
import { generateKey } from "crypto";
import MPKArea from "../Body/MPKArea";
import { ArrowUpRight, Eye, Loading } from "../../svg/Icons";
import { Buffer } from 'buffer'
import { useResetDemo } from "../ResetDemo";
import GoogleAnalytics from "../GA";


const ProofPossession = () => {

    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const [idToken, setIdToken] = useState<string>("");
    const [idTokenParsed, setIdTokenParsed] = useState<any>(null);

    const [showKPop, setShowKPop] = useState(false);
    const [unblindedClaims, setUnblindedClaims] = useState<any>({});
    const [unblindPerformed, setUnblindPerformed] = useState<boolean>(false);
    const [loadingUnblind, setLoadingUnblind] = useState<boolean>(false);

    const [sessionId, setSessionId] = useLocalStorage("sessionId");
    const genRanHex = (size:number) => [...Array(size)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');

    const [kPop, setKPop] = useState<string>("");
    const [errorKPop, setErrorKPop] = useState<boolean>(false);
    const resetDemo = useResetDemo();

    useEffect(() => {

        let tokenStr = searchParams.get("id_token");
        setIdToken(tokenStr || "");

        if (tokenStr && tokenStr !== "") {
            let splitArr = tokenStr.split(".")
            if (splitArr.length === 3) {
                try {
                    let decoded = JSON.parse(Buffer.from(splitArr[1], 'base64').toString());
                    setIdTokenParsed(decoded);
                    //console.log("ID Token is parsed!");
                } catch (e) {
                    console.log("ID Token parsing failed ",e);
                    setIdTokenParsed(null);
                }
            } else {
                console.log("ID Token length is not 3");
                setIdTokenParsed(null);
            }
        } else {
            console.log("ID Token is empty");
            setIdTokenParsed(null);
        }
    }, [searchParams])

    const unBlind = () => {
        setLoadingUnblind(true);
        if (sessionId === "" || !idTokenParsed) {
            setLoadingUnblind(false);
            return;
        }

        let apiUrl = (process.env.REACT_APP_SD_API_URL) || "http://localhost:4000/api/v1";
        let apiPrefix = process.env.REACT_APP_SD_DERIVE_DECRYPTION_KEYS_ENDPOINT || "/derive_decryption_keys";

        let claimName = process.env.REACT_APP_SD_CUSTOM_CLAIM_NAME || "";

        if (!idTokenParsed || !idTokenParsed[claimName] || !idTokenParsed[claimName]["sdp"] || !idTokenParsed[claimName]["sdp"]["bc"]) {
            setLoadingUnblind(false);
            return;
        }

        let claimsToBlind: string[] = [];
        let sdpBc = idTokenParsed[claimName]["sdp"]["bc"];

        Object.keys(sdpBc).forEach(key => {
            claimsToBlind.unshift(key);
        })

        fetch(`${apiUrl+apiPrefix}?user_id=${sessionId}`,{
            method: "POST",
            headers: new Headers({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                credential:  (idTokenParsed && idTokenParsed[claimName]) ? idTokenParsed[claimName] : {},
                claims_to_blind: claimsToBlind
            })
        })
            .then(res => res.json())
            .then(decryptionKeys => {
                // UNBLIND
                apiPrefix = process.env.REACT_APP_SD_UNBLIND_ENDPOINT || "/unblind";
                fetch(`${apiUrl+apiPrefix}`, {
                    method: "POST",
                    headers: new Headers({
                        "Content-Type": "application/json",
                    }),
                    body: JSON.stringify({
                        credential:  (idTokenParsed && idTokenParsed[claimName]) ? idTokenParsed[claimName] : {},
                        decryption_keys: decryptionKeys
                    })
                })
                    .then(res => res.json())
                    .then(credential => {
                        if (idTokenParsed){
                            setLoadingUnblind(false);
                            setUnblindPerformed(true);
                            // const updatedToken = { ...idToken, [claimName]: credential };
                            setUnblindedClaims(credential.credentialSubject);
                        }
                    }).catch(err => {
                        console.log(err); 
                        setLoadingUnblind(false);
                        navigate("/idt/v2/error",{state:{"error": err}});
                    });
            })
            .catch(err => {
                console.log(err);
                setLoadingUnblind(false);
                navigate("/idt/v2/error",{state:{"error": err}});
            });
    }


    const generateKPop = () => {

        let apiPrefix = process.env.REACT_APP_ARKG_API_URL;
        let nonce = ( genRanHex(64));
        fetch(`${apiPrefix}/wallet/generate_proof?user_id=${sessionId}`,{
            method: "POST",
            headers: new Headers({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
            nonce: nonce, // zgeneriraš nek random string (lahko tako kot sem zgeneriral session ID na frontendu)
            id_token: idToken
            })
        })
            .then(res => res.json())
            .then(json => {
                //console.log("jwt: ", json);
                setShowKPop(true);
                setKPop(json.jwt);
            })
            .catch(err => {
                console.log(err);setErrorKPop(true)
                navigate("/idt/v2/error",{state:{"error": err}});
            });
    }

    const inspectKPop = () => {
        const jwtURL = `https://jwt.io/#id_token=${encodeURIComponent(kPop)}`;
        window.open(jwtURL, "_blank");
    }

    function resetDemoCode(){
        resetDemo();
    }

    return (

        <div className="relative flex flex-col w-full max-w-screen-xl gap-10 border-t-1 border-gray-200 mt-10">

            <div className="flex flex-col gap-4">
                <StepSubheader step='step_6_proof_of_possession' />
                <StepDesc step='step_6_proof_of_possession' />
                <p className='text-gray-500 leading-loose text-base font-normal'>Your wallet keeps your credentials secure, maintaining complete control over private keys.</p>
                <MPKArea mpk={ idTokenParsed && idTokenParsed !== null ? (JSON.stringify(idTokenParsed["mynext.id.credential"].cnf, null, 2)) : "{}"} title={"PROOF-OF-POSSESSION KEY"}/>
            </div>

            <div className="flex flex-col gap-4">
                <StepSubheader step='step_6_blinded_claims' />
                {/*<StepDesc step='step_6_blinded_claims' />*/}
                <p className='text-gray-500 leading-loose text-base font-normal'>For added privacy, certain claims in your IDT++ tokens are blinded by the Authorization Server using advanced cryptographic techniques: <span  className="font-bold text-base text-primary-600 ">ECDH (Elliptic-Curve Diffie-Hellman) </span> derives encryption/decryption keys. <span  className="font-bold text-base text-primary-600 ">ChaCha20</span> encrypts the claims for secure handling. Only your wallet or the Authorization Server can unblind these claims, ensuring data privacy.</p>
                
                <MPKArea mpk={ idTokenParsed && idTokenParsed !== null ? (JSON.stringify(idTokenParsed["mynext.id.credential"].credentialSubject, null, 2)) : "{}"} title={"BLINDED CLAIMS"}/>
                <div>
                    <button onClick={() => {unBlind(); GoogleAnalytics("Request IDT", "btn_unbind", "Button to unbind claims")}} disabled={unblindPerformed || loadingUnblind} className="button-secondary ">
                        {!loadingUnblind ? <><Eye className="size-5 "/>Unblind claims</> : <><Loading/>Unblinding claims</>} 
                        
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-4">
                <StepSubheader step='step_6_unblinded_claims' />
                <StepDesc step='step_6_unblinded_claims' />
                <MPKArea mpk={ unblindedClaims ? JSON.stringify(unblindedClaims, null, 2) : "{}"} title={"UNBLINDED CLAIMS"}/>
                {unblindedClaims && unblindPerformed && <p className='text-gray-500 leading-loose text-base font-normal'>Your wallet has successfully unblinded the claims, enabling you to share the blinded claims with a RP.</p>}
         
            </div>

            <div className="flex flex-col gap-4">
                <StepHeader step='step_6_generate_proof_key' />

                <div>
                    <StepDesc step='step_6_generate_proof_key' />
                    <p className='text-gray-500 leading-loose text-base font-normal'>Click <span  className="font-bold text-base text-primary-600" >Generate K-PoP</span> to create your proof and demonstrate control.</p>
                </div>
                
                <div className="flex flex-col gap-2 xsm:flex-row xsm:gap-4">
                    <button
                        onClick={() => {generateKPop();GoogleAnalytics("Request IDT", "btn_generateKPop", "Button to generate KPOP")}} className="button-primary my-2 dark:bg-primary-800">
                        Generate K-PoP
                    </button>

                    {showKPop &&
                        <div>
                            <button onClick={() => {inspectKPop(); GoogleAnalytics("Request IDT", "btn_inspectKPop", "Button to inspect KPOP in jwt.io")}} className="flex gap-2 my-2 text-sm font-medium text-primary-700 pr-5 py-2.5 items-center min-w-[140px] text-center justify-center align-middle">
                            Inspect the K-PoP <ArrowUpRight className='size-4 text-primary-700' />
                            </button>
                        </div>
                    }
                </div>


                {showKPop && (
                    <div>
                        <MPKArea mpk={kPop} title={"KEY - PROOF OF POSSESSION"} error={errorKPop}/>
                        {errorKPop && <p className="text-red-700 text-sm font-normal mt-2">Incorrect key. Please <span className="underline decoration-1 cursor-pointer" onClick={() => {resetDemoCode();GoogleAnalytics("Request IDT", "btn_resetDemo", "Button to reset demo")}}>go back and generate a new MPK</span>. </p>}

                    </div>
                )}
            </div>
        </div>
    );
}

export default ProofPossession;

